import { mapObject } from "~/utils/mapObject"

import treeShake from "./shallowTreeShake"

export type Accessor<Model, UpdateInputValue> = (
  model: Model,
  key: keyof Model,
) => UpdateInputValue

export type Accessors<Model, UpdateInput> = {
  [K in keyof UpdateInput]: Accessor<Model, UpdateInput[K]>
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const isNullish = (value: any): value is null | undefined =>
  [null, undefined].includes(value)

const hasId = (value: any): value is { id: string } =>
  value &&
  typeof value === `object` &&
  `id` in value &&
  typeof value.id === `string`

const hasTypeName = (value: any): value is { __typename: string } =>
  value &&
  typeof value === `object` &&
  `__typename` in value &&
  typeof value.__typename === `string`
/* eslint-enable @typescript-eslint/no-explicit-any */

export const idFor =
  <Model>(key: keyof Model) =>
  (model: Model): string | null | undefined => {
    const value = model[key]
    if (isNullish(value)) {
      return value
    }
    if (hasId(value)) {
      return value.id
    }
    throw new Error(`${String(key)} lacks an id`)
  }

export const get = <Model, K extends keyof Model>(
  model: Model,
  key: K,
): Model[K] => model[key]

export const getEach = <Model, K extends keyof Model>(
  models: Model[],
  key: K,
): Model[K][] => models.map((m) => get(m, key))

// export const getObject = <Model, K extends keyof Model>(
//   model: Model,
//   key: K
// ) => {
//   const value = model[key];
//   if (isNullish(value)) {
//     return value;
//   }
//   if (hasTypeName(value)) {
//     const { __typename, ...input } = value;
//     return input;
//   }
//   throw new Error(`${value} lacks a __typename`);
// };

export const createInputBuilder =
  <Model, UpdateInput>(accessors: Accessors<Model, UpdateInput>) =>
  (delta: Partial<Model>, id: string): UpdateInput => {
    // @ts-expect-error typescript struggles to understand this situation
    const input = mapObject(accessors, (accessor, key) => accessor(delta, key))

    return treeShake({ ...input, id }) as UpdateInput
  }
